import MessageBroker from '@Services/MessageBroker';
import RequestUrls from '@Constants/RequestUrlsConstants';
import { reactions } from '@Constants';
import { imageTypes } from '@Constants/rx-ui/constants';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import { getThumbnailByType } from '@Services/RxThumbnail/RxThumbnail';
import UserDatabases from '@Services/UserDatabases';
import { noCache } from '@Utils/Network/Network';

const DEFAULT_MAX_RESULTS = 20;
const DEFAULT_PROJECT_PAGE = 0;
const DEFAULT_PROJECT_PAGE_SIZE = 30;
const DEFAULT_PROJECT_SORT = 'created,DESC';
const CONDITIONS_REACTION_IMAGE_LABEL = 'No image';
const TOP_N = 10;
const RANK_FUNCTIONAL_GROUP_CACHE_LIMIT = 10;

// Maps the API similarity buckets to human-readable tab/level names.
// Applied once here (AR-3.1) so no component does its own RC-level mapping.
const RC_LEVEL_NAME = { 2: 'tight', 1: 'medium', 0: 'wide' };

export const SOURCE_LABEL = {
  retrosynthesis_step: 'Retrosynthesis step',
  reaxys_reaction: 'Reaxys reaction',
  standalone: 'My drawn reaction',
  hopinto: 'External link',
};

const rankFunctionalGroupsCache = new Map();

function toArray(value) {
  return Array.isArray(value) ? value : [];
}

function formatDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

function getNumberFromId(id) {
  const number = parseInt(String(id || '').replace(/\D/g, ''), 10);
  return Number.isNaN(number) ? 0 : number;
}

function uniqueReactionCount(predictions) {
  const ids = predictions.reduce((acc, prediction) => {
    toArray(prediction?.reactionIds).forEach((id) => acc.add(id));
    return acc;
  }, new Set());

  return ids.size;
}

function getProjectResponse(project = {}) {
  return project?.response || project?.predictionSnapshot || null;
}

// The FE stores `reference` as stringified JSON during creation. Parse it back to
// extract referenceType and the source identifiers.
function parseReference(reference) {
  if (!reference || typeof reference !== 'string') return null;
  try {
    const parsed = JSON.parse(reference);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function normalizeSubstance({ name, rxid } = {}) {
  return { name, id: rxid ?? null };
}

function normalizeProject(project = {}) {
  const response = getProjectResponse(project);
  const predictions = toArray(response?.predictions);
  const parsedRef = parseReference(project?.reference);
  const referenceType = parsedRef?.type || project?.referenceType || 'standalone';
  const referenceLabel = project?.referenceLabel || SOURCE_LABEL[referenceType] || referenceType;

  const sourceReactionId = referenceType === 'reaxys_reaction' ? parsedRef?.reactionId || null : null;
  const sourceProjectId = referenceType === 'retrosynthesis_step' ? parsedRef?.projectId || null : null;
  const sourceRouteId = referenceType === 'retrosynthesis_step' ? parsedRef?.routeId || null : null;
  const sourceStepId = referenceType === 'retrosynthesis_step' ? parsedRef?.step || null : null;

  return {
    ...project,
    response,
    predictionSnapshot: response,
    id: project?.id,
    number: getNumberFromId(project?.id),
    date: formatDate(project?.created || project?.modified),
    created: project?.created,
    modified: project?.modified,
    projectName: project?.name,
    referenceType,
    referenceLabel,
    sourceReactionId,
    sourceProjectId,
    sourceRouteId,
    sourceStepId,
    reactionString: project?.reactionStructure || project?.smilesStructure || response?.reaction?.rxnFile || null,
    conditionSets: project?.conditionSets ?? predictions.length,
    uniqueReactions: project?.uniqueReactions ?? uniqueReactionCount(predictions),
    resultCount: project?.resultCount ?? project?.conditionSets ?? predictions.length,
    warnings: toArray(response?.warnings),
  };
}

function getProjectsPageFromResponse(response = {}) {
  const items = toArray(response.content).map(normalizeProject);

  return {
    items,
    totalItems: response.totalElements,
    totalPages: response.totalPages,
    page: response.number,
    size: response.size,
  };
}

function getRcLevel(value) {
  if (value == null || value === '') return '';
  const normalized = String(value).toLowerCase().replace('rc', '');
  return RC_LEVEL_NAME[normalized] || '';
}

// Yield sorts last when blank (FR-3.3), so blank values are stored as null rather
// than a formatted string. The detail page formats the percentage on render.
function getYieldPercent(value) {
  if (value == null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function getLabilityPercent(value) {
  if (value == null || value === '') return null;
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  const percent = number > 1 ? number : number * 100;
  return Math.round(Math.max(0, Math.min(100, percent)));
}

function getCount(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function normalizeFunctionalGroupLability(item = {}) {
  const reactedPercent = getLabilityPercent(item?.lability);
  const reactedCount = getCount(item?.totalChanged);
  const unreactedCount = getCount(item?.totalPreserved);

  return {
    fgId: item?.fgId ?? null,
    label: item?.fgName || String(item?.fgId || ''),
    reactedPercent,
    unreactedPercent: reactedPercent == null ? null : 100 - reactedPercent,
    reactedCount,
    unreactedCount,
    reactedReactionIds: toArray(item?.reactedReactionIds),
    unreactedReactionIds: toArray(item?.unreactedReactionIds),
    total: reactedCount + unreactedCount,
  };
}

function getRankFunctionalGroupsCacheKey(projectId, rankId) {
  return `${projectId}:${rankId}`;
}

function setCachedRankFunctionalGroups(cacheKey, value) {
  rankFunctionalGroupsCache.delete(cacheKey);
  rankFunctionalGroupsCache.set(cacheKey, value);

  while (rankFunctionalGroupsCache.size > RANK_FUNCTIONAL_GROUP_CACHE_LIMIT) {
    const cacheKeys = rankFunctionalGroupsCache.keys();
    const oldestKey = cacheKeys.next().value;
    rankFunctionalGroupsCache.delete(oldestKey);
  }
}

function normalizePrediction(prediction = {}) {
  return {
    ...prediction,
    rank: prediction?.rank,
    reagentsCatalysts: toArray(prediction?.reagents).map(normalizeSubstance),
    solvents: toArray(prediction?.solvents).map(normalizeSubstance),
    reactionCount: prediction?.reactionCount ?? 0,
    reactionIds: toArray(prediction?.reactionIds),
    rcLevel: getRcLevel(prediction?.rcLevel),
    yieldPercent: getYieldPercent(prediction?.medianYield),
    fgLability: toArray(prediction?.fgLability).map(normalizeFunctionalGroupLability),
  };
}

function toSummaryEntries(bucket) {
  if (Array.isArray(bucket)) {
    return bucket.map((item) => ({
      name: item?.name,
      id: item?.rxid ?? null,
      count: Number(item?.count ?? 0),
    }));
  }
  return [];
}

function normalizeSummaryItems(bucket) {
  return toSummaryEntries(bucket)
    .map(({ name, id, count }) => ({ name, id, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, TOP_N);
}

function getSummaryBucket(group, fieldName, legacyFieldName) {
  // TODO: Remove the legacy rc0/rc1/rc2 fallback after saved project snapshots are migrated.
  return group?.[fieldName] ?? group?.[legacyFieldName];
}

function normalizeSummaryGroup(group = {}) {
  return {
    Tight: normalizeSummaryItems(getSummaryBucket(group, 'rc2_tight', 'rc2')),
    Medium: normalizeSummaryItems(getSummaryBucket(group, 'rc1_medium', 'rc1')),
    Wide: normalizeSummaryItems(getSummaryBucket(group, 'rc0_wide', 'rc0')),
  };
}

function normalizeSummary(statistics = {}) {
  const matchCounts = toArray(statistics?.rcMatchCounts?.reactions);

  return {
    topReagents: normalizeSummaryGroup(statistics?.reagents),
    topSolvents: normalizeSummaryGroup(statistics?.solvents),
    matchDistribution: [
      { name: 'Wide', count: Number(matchCounts[0] || 0) },
      { name: 'Medium', count: Number(matchCounts[1] || 0) },
      { name: 'Tight', count: Number(matchCounts[2] || 0) },
    ],
  };
}

function buildReferenceString(reference) {
  return typeof reference === 'string' ? reference : JSON.stringify(reference ?? { type: 'standalone' });
}

export async function predictConditions(reaction, { maxResults = DEFAULT_MAX_RESULTS, reference, name, validErrorStatusCodes } = {}) {
  return MessageBroker.send(
    // validErrorStatusCodes lets callers (e.g. hopinto) handle specific error statuses
    // themselves instead of triggering the generic error modal
    validErrorStatusCodes ? { ...RequestUrls.conditionNavigatorPredict, validErrorStatusCodes } : RequestUrls.conditionNavigatorPredict,
    {
      reaction,
      maxResults,
      ...(reference == null ? {} : { reference: buildReferenceString(reference) }),
      ...(name == null ? {} : { name }),
    },
    noCache()
  );
}

export async function getProjectsPage({ page = DEFAULT_PROJECT_PAGE, size = DEFAULT_PROJECT_PAGE_SIZE, sort = DEFAULT_PROJECT_SORT } = {}) {
  const response = await MessageBroker.send(RequestUrls.conditionNavigatorProjects, { page, size, sort }, noCache());
  return getProjectsPageFromResponse(response);
}

export async function getProjects() {
  const page = await getProjectsPage();
  return page.items;
}

export async function getConditionsExportAttempts() {
  const response = await MessageBroker.send(
    RequestUrls.exportOutputFormats,
    {
      databaseId: UserDatabases.getDefaultDatabase()?.id,
      context: reactions,
      main: reactions,
    },
    { cache: false }
  );
  const attempts = {};

  if (Object.prototype.hasOwnProperty.call(response, 'exportAttemptsLimit')) {
    attempts.exportAttemptsLimit = response.exportAttemptsLimit;
  }
  if (Object.prototype.hasOwnProperty.call(response, 'exportAttemptsCurrentlyUsed')) {
    attempts.exportAttemptsCurrentlyUsed = response.exportAttemptsCurrentlyUsed;
  }

  return attempts;
}

export async function getProject(id) {
  const project = await MessageBroker.send(RequestUrls.conditionNavigatorProject, { id }, noCache());
  const normalizedProject = normalizeProject(project);
  const snapshot = getProjectResponse(project) || {};

  return {
    run: normalizedProject,
    recommendations: toArray(snapshot?.predictions).map(normalizePrediction),
    summary: normalizeSummary(snapshot?.statistics),
  };
}

export function clearProjectRankFunctionalGroupsCache() {
  rankFunctionalGroupsCache.clear();
}

export async function getProjectRankFunctionalGroups(projectId, rankId) {
  if (!projectId || rankId == null || rankId === '') {
    throw new Error('A project ID and rank are required to load functional group reactions.');
  }

  const cacheKey = getRankFunctionalGroupsCacheKey(projectId, rankId);
  const cached = rankFunctionalGroupsCache.get(cacheKey);

  if (cached) {
    return cached;
  }

  const request = MessageBroker.send(RequestUrls.conditionNavigatorProjectRank, { projectId, rankId }).then((response) =>
    toArray(response).map(normalizeFunctionalGroupLability)
  );

  setCachedRankFunctionalGroups(cacheKey, request);

  try {
    return await request;
  } catch (error) {
    rankFunctionalGroupsCache.delete(cacheKey);
    throw error;
  }
}

export async function getReactionImageUrl(reaction, imageType = imageTypes.CONDITIONS_NAVIGATOR_L) {
  if (!reaction) return '';

  const image = await getThumbnailByType(reaction, imageType, CONDITIONS_REACTION_IMAGE_LABEL);

  return image?.url || '';
}

export async function validateRetrosynthesisRouteSource({ projectId, routeId }) {
  return MessageBroker.send(RequestUrls.rsProjectRoute, { projectId, routeId }, noCache());
}

export async function updateProject(id, fields) {
  const updated = await MessageBroker.send(RequestUrls.conditionNavigatorUpdateProject, { id }, noCache(), {
    name: fields?.name,
    reference: fields?.reference ?? fields?.referenceLabel,
  });
  return normalizeProject({ id, ...fields, ...(updated && typeof updated === 'object' ? updated : {}) });
}

export async function deleteProject(id) {
  return MessageBroker.send(RequestUrls.conditionNavigatorDeleteProject, { id }, noCache());
}

// AR-1.2: single internal function that calls the BE predict-create API. Source
// wrappers below stay stable and do not know the request/response details.
export async function createProjectFromReaction({ reaction, name, referenceType, reference, referenceLabel, validErrorStatusCodes }) {
  const type = referenceType || 'standalone';
  const label = referenceLabel || name || SOURCE_LABEL[type] || SOURCE_LABEL.standalone;
  const created = await predictConditions(reaction, {
    maxResults: DEFAULT_MAX_RESULTS,
    reference: reference ?? { type },
    name,
    validErrorStatusCodes,
  });

  return normalizeProject({
    ...created,
    referenceLabel: created?.referenceLabel || label,
  });
}

export async function createProjectFromDrawnReaction({ reaction }) {
  return createProjectFromReaction({
    reaction,
    referenceType: 'standalone',
    reference: { type: 'standalone' },
    referenceLabel: SOURCE_LABEL.standalone,
  });
}

export async function createProjectFromReaxysReaction({ reactionId, rxnFile }) {
  return createProjectFromReaction({
    reaction: rxnFile,
    referenceType: 'reaxys_reaction',
    reference: { type: 'reaxys_reaction', reactionId: String(reactionId) },
    referenceLabel: `Reaxys reaction RX.ID ${reactionId}`,
  });
}

export async function createProjectFromRtStep({ projectId, routeId, step }) {
  const rxnResponse = await MessageBroker.send(RequestUrls.rsProjectsGetRouteStepMollFile, { projectId, routeId, step }, noCache());
  const reaction = typeof rxnResponse === 'string' ? rxnResponse : rxnResponse?.structure || rxnResponse?.rxnFile || '';

  if (!reaction) {
    NotifyDialogsService.openModalWindow({
      type: 'error',
      title: 'Condition search failed',
      text: 'A reaction structure is not available for this Retrosynthesis step.',
      confirmText: 'Close',
    });
    throw new Error('Retrosynthesis step reaction string is empty.');
  }

  return createProjectFromReaction({
    reaction,
    referenceType: 'retrosynthesis_step',
    reference: { type: 'retrosynthesis_step', projectId: String(projectId), routeId: String(routeId), step: String(step) },
    referenceLabel: `Project ${projectId} / Route ${routeId} / Step ${step}`,
  });
}
